<script setup lang="ts">
import {onBeforeMount, onMounted, PropType, Ref, ref, useTemplateRef} from "vue";
import {ICellEditorParams} from "ag-grid-community";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import {QSelect} from "quasar";

import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import GUIUtilities, {GoogleMaterialIcon} from "@/utils/common/GUIUtilities.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";

const props = defineProps({
  params: {
    type: Object as PropType<ICellEditorParams>,
    required: true
  }
});
let allowedSearchItems: GoogleMaterialIcon[] = [];
const selectedItem: Ref<GoogleMaterialIcon | null | undefined> = ref({name: props.params.value})
const options = ref<GoogleMaterialIcon[]>([selectedItem.value!]);
const qSelect = useTemplateRef<QSelect>("qSelect");
const popupVisible = ref(false);
const displayedText = ref<string | null | undefined>(props.params.value);

onBeforeMount(async () => {
  let icons = await GUIUtilities.fetchIconList() ?? [];
  allowedSearchItems.push(...icons);
  options.value = fetchFilteredItems(props.params.value);
  selectedItem.value = props.params.value ? allowedSearchItems.find(l => l.name === props.params.value) : null;
})

function fetchFilteredItems(value: string | null | undefined): GoogleMaterialIcon[] {

  if (!value)
    return allowedSearchItems;
  return allowedSearchItems.filter(e =>
    (e.name.includes(value) || e.name.includes(StringUtils.arabicToEnglish(StringUtils.replaceArNumerals(value)))) ||
    (e.tags && e.tags.length && e.tags.contains(StringUtils.arabicToEnglish(StringUtils.replaceArNumerals(value)))))
    .sort((a, b) => a.name === b.name ? 0 : a.name!.length - b.name!.length);
}

function filter(value: string | null | undefined) {
  options.value = fetchFilteredItems(value);
}

function onFilter(val, update) {
  update(() => filter(val),
    ref => {
      if (val !== '' && ref.options.length > 0) {
        ref.setOptionIndex(-1);
        ref.moveOptionSelection(1, true);
      }
    })
}

function getValue() {
  return selectedItem.value?.name;
}

onMounted(() => {
  if (qSelect.value) {
    setTimeout(() => {
      qSelect.value?.focus();
      if (props.params?.eventKey?.length === 1) {
        qSelect.value?.updateInputValue(props.params?.eventKey)
      }
    })
  }
});

function onPartialUserInput(val) {
  if (!val)
    selectedItem.value = null;
  displayedText.value = val;
}

function onSelect(value?: GoogleMaterialIcon) {
  selectedItem.value = value;
  props.params?.stopEditing();
  AGGridUtils.navigateToNextCellAndStartEditing(props.params!.api);
}

function onEnterKeyDown(event: KeyboardEvent) {
  if (!popupVisible.value)
    return;
  if (AGGridUtils.isBulkAction(event)) {
    namaUpdateValue(fetchFilteredItems(qSelect.value?.modelValue)[0].name);
    AGGridUtils.dispatchBulkEventFor(qSelect);
  }
  else {
    props.params?.stopEditing();
    selectedItem.value = null;
    props.params?.stopEditing();
    AGGridUtils.navigateToNextCellAndStartEditing(props.params?.api);
  }
}

function namaUpdateValue(value: string) {
  selectedItem.value = value ? allowedSearchItems.find(l => l.name === value) : undefined;
  displayedText.value = selectedItem.value?.name;
}

function onKeyDown(event: KeyboardEvent) {
  if (event.key === 'F2') {
    let inputFieldValue = displayedText.value;
    if ((!inputFieldValue && selectedItem.value) || (selectedItem.value && selectedItem.value.name === inputFieldValue))
      return;
    selectedItem.value = inputFieldValue ? allowedSearchItems.find(l => allowedSearchItems.find(l => l.name === inputFieldValue)) : undefined;
    props.params.api.stopEditing();
  } else if (event.key === 'Escape') {
    props.params.api.stopEditing();
    props.params.api.setFocusedCell(props.params.rowIndex, props.params.column);
  }
}

defineExpose({getValue, namaUpdateValue});
</script>

<template>
  <div class="tw-mb-0 tw-h-full grid-enum-selector tw-max-w-full tw-w-full" @keydown="onKeyDown">
    <div class="tw-relative tw-leading-tight tw-h-full tw-max-w-full tw-w-full">
      <QSelect
        ref="qSelect"
        @keydown.enter="onEnterKeyDown"
        use-input hide-selected fill-input dense options-dense
        input-debounce="0"
        :options="options"
        option-label="name"
        option-value="name"
        @filter="onFilter"
        @input-value="onPartialUserInput"
        :model-value="displayedText"
        @popup-hide="popupVisible = false"
        @popup-show="popupVisible = true"
        @update:model-value="onSelect"
        class="tw-bg-[#F8F9FA] tw-shadow tw-appearance-none dark:tw-bg-[#3E3E3E] tw-h-full tw-min-h-full ag-custom-component-popup"
        input-class="tw-bg-transparent focus:tw-ring-0 focus:tw-shadow-none focus:tw-border-none dark:tw-text-namaFontColor-input-dark
            dark:tw-placeholder-gray-300"
        popup-content-class="!tw-max-h-[30vh] navbar-search-popup dark:tw-bg-namaBackground-deep-dark dark:tw-text-namaFontColor-input-dark ag-custom-component-popup">
        <template v-slot:option="scope">
          <q-item v-bind="scope.itemProps" :class="{'selected-list-item':
                              selectedItem && selectedItem.name === scope.opt.name}">
            <q-item-section>
              <div class="tw-flex tw-items-center tw-gap-2" dir="ltr">
                <NamaIcon :default-icon="scope.opt.name" :key="scope.opt.name"
                          :class="{'active-white':selectedItem && selectedItem.name === scope.opt.name}"/>
                <span v-text="scope.opt.namaIconName??scope.opt.name.replaceAll('_',' ')" class="tw-text-sm dark:tw-text-namaFontColor-input-dark"
                      :class="{'tw-text-namaFontColor-input-dark':selectedItem && selectedItem.name === scope.opt.name}"/>
              </div>
            </q-item-section>
          </q-item>
        </template>
      </QSelect>
    </div>
  </div>
</template>

<style>
</style>
